import { DESIGN_TOKENS, IconCheck } from '@myplayer/ui';
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './auth-context.js';
import {
  AuthScreenShell,
  BrandedEmailPreviewCard,
  ChangePasswordModal,
  ChangePasswordPage,
} from './auth-password-modal.js';
import { Button, Input } from './components/ui/primitives.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';
import { resolveAdminEmailPlaceholder } from './use-workspace-identifiers.js';

export { AuthScreenShell, BrandedEmailPreviewCard, ChangePasswordModal, ChangePasswordPage };

export function ResetPasswordPage(props?: { readonly oobCode?: string }): ReactElement {
  const { sendPasswordReset, passwordResetSent, verifyResetCode, confirmPasswordResetAction } =
    useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeOobCode = (props?.oobCode ?? searchParams.get('oobCode') ?? '').trim();

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [verifyingCode, setVerifyingCode] = useState<boolean>(activeOobCode.length > 0);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completedReset, setCompletedReset] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (activeOobCode.length === 0) {
      setVerifyingCode(false);
      return;
    }
    let cancelled = false;
    setVerifyingCode(true);
    setLocalError(null);
    verifyResetCode(activeOobCode)
      .then((resolvedEmail) => {
        if (!cancelled) {
          setVerifiedEmail(resolvedEmail);
          setVerifyingCode(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLocalError(
            formatPlainAdminError(err, ADMIN_STRINGS.errors.resetPasswordActionFallback),
          );
          setVerifyingCode(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeOobCode, verifyResetCode]);

  const handleRequestResetSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setLocalError(null);
    setSubmitting(true);
    try {
      await sendPasswordReset(email);
    } catch (err: unknown) {
      setLocalError(formatPlainAdminError(err, ADMIN_STRINGS.errors.signInPasswordResetFallback));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmResetSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setLocalError(null);
    if (newPassword.trim().length < 8) {
      setLocalError('Please choose a new password with at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError('Your new password and confirmation password do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordResetAction(activeOobCode, newPassword);
      setCompletedReset(true);
    } catch (err: unknown) {
      setLocalError(formatPlainAdminError(err, ADMIN_STRINGS.errors.resetPasswordActionFallback));
    } finally {
      setSubmitting(false);
    }
  };

  if (activeOobCode.length > 0) {
    return (
      <AuthScreenShell
        title="Choose a new password"
        subtitle={
          verifiedEmail
            ? `Set a new password for ${verifiedEmail}`
            : 'Set a new password for your MyPlayer Admin Console account'
        }
        badgeLabel="Password Reset"
      >
        {verifyingCode ? (
          <p className="py-4 text-center text-[13px] text-fg-secondary">
            Checking your password reset link...
          </p>
        ) : completedReset ? (
          <div className="space-y-4" data-testid="reset-password-complete">
            <div className="rounded-control border border-status-valid-border bg-status-valid-bg p-3 text-[13px] text-status-valid">
              Your password has been updated. You can now sign in with your new password.
            </div>
            <Button variant="primary" className="w-full" onClick={() => navigate('/sign-in')}>
              Return to sign in
            </Button>
          </div>
        ) : (
          <form onSubmit={(e) => void handleConfirmResetSubmit(e)} className="space-y-4">
            <div className="space-y-1.5">
              <label
                htmlFor="reset-new-password"
                className="block text-[12px] font-medium text-fg-secondary"
              >
                New password
              </label>
              <Input
                id="reset-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="reset-confirm-password"
                className="block text-[12px] font-medium text-fg-secondary"
              >
                Confirm new password
              </label>
              <Input
                id="reset-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                required
              />
            </div>
            {localError !== null && (
              <p
                role="alert"
                className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
              >
                {localError}
              </p>
            )}
            <div className="flex flex-col gap-2 pt-1 sm:flex-row">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={() => navigate('/sign-in')}
              >
                Back to sign in
              </Button>
              <Button type="submit" variant="primary" className="flex-1" disabled={submitting}>
                {submitting ? 'Saving password...' : 'Save new password'}
              </Button>
            </div>
          </form>
        )}
      </AuthScreenShell>
    );
  }

  return (
    <AuthScreenShell
      title="Reset your password"
      subtitle="Enter your work email and we will send you a link to choose a new password."
      badgeLabel="Password Reset"
    >
      <form onSubmit={(e) => void handleRequestResetSubmit(e)} className="space-y-4">
        <div className="space-y-1.5">
          <label
            htmlFor="reset-request-email"
            className="block text-[12px] font-medium text-fg-secondary"
          >
            Work Email
          </label>
          <Input
            id="reset-request-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={resolveAdminEmailPlaceholder('admin')}
          />
        </div>
        {passwordResetSent && (
          <div className="space-y-2">
            <p className="rounded-control border border-status-valid-border bg-status-valid-bg p-3 text-[12px] text-status-valid">
              Password reset email sent! Check your inbox for reset instructions.
            </p>
            <BrandedEmailPreviewCard templateId="PASSWORD_RESET" recipientEmail={email} />
          </div>
        )}
        {localError !== null && (
          <p
            role="alert"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
          >
            {localError}
          </p>
        )}
        <div className="flex flex-col gap-2 pt-1 sm:flex-row">
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            onClick={() => navigate('/sign-in')}
          >
            Back to sign in
          </Button>
          <Button type="submit" variant="primary" className="flex-1" disabled={submitting}>
            {submitting ? 'Sending reset link...' : 'Send password reset email'}
          </Button>
        </div>
      </form>
    </AuthScreenShell>
  );
}

export function VerifyEmailPage(props?: { readonly oobCode?: string }): ReactElement {
  const { session, applyEmailVerificationCode, sendVerificationEmail, verificationEmailSent } =
    useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeOobCode = (props?.oobCode ?? searchParams.get('oobCode') ?? '').trim();

  const [verifying, setVerifying] = useState<boolean>(activeOobCode.length > 0);
  const [verified, setVerified] = useState<boolean>(Boolean(session?.emailVerified));
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (activeOobCode.length === 0) {
      setVerifying(false);
      return;
    }
    let cancelled = false;
    setVerifying(true);
    setLocalError(null);
    applyEmailVerificationCode(activeOobCode)
      .then(() => {
        if (!cancelled) {
          setVerified(true);
          setVerifying(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLocalError(formatPlainAdminError(err, ADMIN_STRINGS.errors.verifyEmailActionFallback));
          setVerifying(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeOobCode, applyEmailVerificationCode]);

  const handleResendVerification = async (): Promise<void> => {
    setLocalError(null);
    setSendingEmail(true);
    try {
      await sendVerificationEmail();
    } catch (err: unknown) {
      setLocalError(formatPlainAdminError(err, ADMIN_STRINGS.errors.verificationEmailFallback));
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <AuthScreenShell
      title={verified ? 'Email address verified' : 'Verify your work email'}
      subtitle={
        verified
          ? 'Your MyPlayer Admin Console email is confirmed and ready to use.'
          : 'Confirm your email address so your team knows it is you.'
      }
      badgeLabel="Email Verification"
    >
      <div className="space-y-4">
        {verifying && (
          <p className="py-3 text-center text-[13px] text-fg-secondary">
            Confirming your verification link...
          </p>
        )}

        {verified && !verifying && (
          <div
            data-testid="verify-email-complete"
            className="flex items-start gap-2.5 rounded-control border border-status-valid-border bg-status-valid-bg p-3 text-[13px] text-status-valid"
          >
            <IconCheck />
            <span>
              Thank you—your email address is verified. You can continue to the Admin Console.
            </span>
          </div>
        )}

        {!verified && !verifying && (
          <div className="space-y-3">
            <p className="text-[13px] text-fg-secondary">
              We send a branded verification email from{' '}
              <span className="font-semibold text-fg">{DESIGN_TOKENS.brandIdentity.logoText}</span>{' '}
              with a one-click confirmation button.
            </p>
            <BrandedEmailPreviewCard
              templateId="VERIFY_EMAIL"
              {...(session?.email ? { recipientEmail: session.email } : {})}
            />
          </div>
        )}

        {verificationEmailSent && (
          <p className="rounded-control border border-status-valid-border bg-status-valid-bg p-3 text-[12px] text-status-valid">
            Verification email sent! Check your inbox and click “Verify email address”.
          </p>
        )}

        {localError !== null && (
          <p
            role="alert"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
          >
            {localError}
          </p>
        )}

        <div className="flex flex-col gap-2 pt-1 sm:flex-row">
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            onClick={() => navigate(session ? '/settings' : '/sign-in')}
          >
            {session ? 'Back to settings' : 'Back to sign in'}
          </Button>
          {!verified && (
            <Button
              type="button"
              variant="primary"
              className="flex-1"
              disabled={sendingEmail}
              onClick={() => void handleResendVerification()}
            >
              {sendingEmail ? 'Sending email...' : 'Send verification email'}
            </Button>
          )}
        </div>
      </div>
    </AuthScreenShell>
  );
}

export function FirebaseAuthActionPage(): ReactElement {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') ?? '';
  const oobCode = searchParams.get('oobCode') ?? '';

  if (mode === 'verifyEmail') {
    return <VerifyEmailPage oobCode={oobCode} />;
  }
  return <ResetPasswordPage oobCode={oobCode} />;
}
